import { formatDateBR, formatDateTimeBR } from '../utils/dateUtils';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { EyeIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { assinaturasService, type Invoice } from '../services/assinaturas.service';
import { formatCurrency } from '../utils/formatters';
import DataTable from '../components/DataTable/DataTable';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';
import type { Column } from '../types/data-table';
import { useState } from 'react';

export default function AssinaturaDetalhes() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoicePage, setInvoicePage] = useState(1);
  const [invoiceLimit, setInvoiceLimit] = useState(20);

  const { data: assinatura, isLoading: isLoadingAssinatura } = useQuery({
    queryKey: ['assinatura', id],
    queryFn: () => assinaturasService.findOne(id!),
    enabled: !!id,
  });

  const { data: invoicesResponse, isLoading: isLoadingInvoices } = useQuery({
    queryKey: ['assinatura-invoices', id, invoicePage, invoiceLimit],
    queryFn: () => assinaturasService.getInvoices(id!, { page: invoicePage, limit: invoiceLimit }),
    enabled: !!id,
  });

  const invoices = invoicesResponse?.data || [];
  const invoicePagination = invoicesResponse?.meta || {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      ACTIVE: { label: 'Ativa', className: 'bg-green-100 text-green-800' },
      SUSPENDED: { label: 'Suspensa', className: 'bg-yellow-100 text-yellow-800' },
      CANCELED: { label: 'Cancelada', className: 'bg-red-100 text-red-800' },
      EXPIRED: { label: 'Expirada', className: 'bg-gray-100 text-gray-800' },
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const getBillingCycleLabel = (cycle: string) => {
    const cycles: Record<string, string> = {
      MONTHLY: 'Mensal',
      QUARTERLY: 'Trimestral',
      YEARLY: 'Anual',
    };
    return cycles[cycle] || cycle;
  };

  const getInvoiceStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; className: string }> = {
      PAID: { label: 'Pago', className: 'bg-green-100 text-green-800' },
      PENDING: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800' },
      OVERDUE: { label: 'Vencido', className: 'bg-red-100 text-red-800' },
      CANCELLED: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800' },
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleViewInvoice = (invoice: Invoice) => {
    navigate(`/assinaturas/${id}/faturas/${invoice.id}`);
  };

  const handleDownloadInvoicePDF = async (invoice: Invoice) => {
    if (!id) return;

    try {
      const blob = await assinaturasService.downloadInvoicePDF(id, invoice.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Erro ao baixar PDF:', error);
      alert('Erro ao baixar PDF da fatura');
    }
  };

  const invoiceColumns: Column<Invoice>[] = [
    {
      key: 'invoice_number',
      label: 'Número',
      sortable: true,
    },
    {
      key: 'issue_date',
      label: 'Emissão',
      render: (item) => formatDateBR(item.issue_date),
      sortable: true,
    },
    {
      key: 'due_date',
      label: 'Vencimento',
      render: (item) => formatDateBR(item.due_date),
      sortable: true,
    },
    {
      key: 'base_amount',
      label: 'Valor Base',
      render: (item) => formatCurrency(item.base_amount),
      sortable: false,
    },
    {
      key: 'extra_lives_amount',
      label: 'Vidas Extras',
      render: (item) => formatCurrency(item.extra_lives_amount),
      sortable: false,
    },
    {
      key: 'extra_exams_amount',
      label: 'Exames Extras',
      render: (item) => formatCurrency(item.extra_exams_amount),
      sortable: false,
    },
    {
      key: 'total_amount',
      label: 'Total',
      render: (item) => formatCurrency(item.total_amount),
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => getInvoiceStatusBadge(item.status),
      sortable: true,
    },
    {
      key: 'paid_at',
      label: 'Pagamento',
      render: (item) => item.paid_at
        ? formatDateTimeBR(item.paid_at)
        : '-',
      sortable: false,
    },
  ];

  if (isLoadingAssinatura) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Carregando detalhes da assinatura...</p>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!assinatura) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-600 mb-4">Assinatura não encontrada</p>
            <button
              onClick={() => navigate('/assinaturas')}
              className="btn-primary"
            >
              Voltar para Assinaturas
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={assinatura.name}
        description="Detalhes da assinatura"
        breadcrumbs={[
          { label: 'Assinaturas', href: '/assinaturas' },
          { label: assinatura.name },
        ]}
        actions={getStatusBadge(assinatura.status)}
      />

        {/* Informações Gerais */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Plano</h3>
            <p className="text-2xl font-semibold text-gray-900">
              {assinatura.plan?.name || 'N/A'}
            </p>
            {assinatura.plan?.slug && (
              <p className="text-sm text-gray-500 mt-1">Slug: {assinatura.plan.slug}</p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Ciclo de Cobrança</h3>
            <p className="text-2xl font-semibold text-gray-900">
              {getBillingCycleLabel(assinatura.billing_cycle)}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Vidas Contratadas</h3>
            <p className="text-2xl font-semibold text-gray-900">
              {assinatura.contracted_lives}
            </p>
            {assinatura.current_bracket && (
              <p className="text-sm text-gray-500 mt-1">
                Faixa: {assinatura.current_bracket.min_lives} - {assinatura.current_bracket.max_lives || '∞'} vidas
              </p>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Saldo da Carteira</h3>
            <p className="text-2xl font-semibold text-gray-900">
              {assinatura.is_unlimited_wallet
                ? 'Ilimitado'
                : formatCurrency(Number(assinatura.wallet_amount))}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-medium text-gray-500 mb-2">Data de Início</h3>
            <p className="text-lg font-semibold text-gray-900">
              {formatDateBR(assinatura.started_at)}
            </p>
          </div>

          {assinatura.expires_at && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Data de Expiração</h3>
              <p className="text-lg font-semibold text-gray-900">
                {formatDateBR(assinatura.expires_at)}
              </p>
            </div>
          )}

          {assinatura.canceled_at && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Data de Cancelamento</h3>
              <p className="text-lg font-semibold text-gray-900">
                {formatDateBR(assinatura.canceled_at)}
              </p>
            </div>
          )}

          {assinatura.extra_life_price && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Preço por Vida Extra</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {formatCurrency(Number(assinatura.extra_life_price))}
              </p>
            </div>
          )}
        </div>

        {/* Features do Plano */}
        {assinatura.plan && (
          <div className="bg-white rounded-lg shadow p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Features do Plano</h2>
            {assinatura.plan.features && assinatura.plan.features.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {assinatura.plan.features
                  .filter((f) => f.included)
                  .map((feature) => (
                    <div key={feature.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">
                          {feature.feature_name || feature.exame?.nome || feature.feature_key || 'N/A'}
                        </span>
                        <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                          Incluído
                        </span>
                      </div>
                      {feature.feature_description && (
                        <p className="text-sm text-gray-600 mt-1 mb-2">
                          {feature.feature_description}
                        </p>
                      )}
                      {feature.monthly_limit && (
                        <p className="text-sm text-gray-500 mt-1">
                          Limite mensal: {feature.monthly_limit}
                        </p>
                      )}
                      {feature.extra_cost_per_exam && (
                        <p className="text-sm text-gray-500 mt-1">
                          Custo extra: {formatCurrency(Number(feature.extra_cost_per_exam))}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            ) : (
              <p className="text-gray-500">Nenhuma feature configurada para este plano.</p>
            )}
          </div>
        )}

        {/* Faturas */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Faturas</h2>
          <DataTable
            data={invoices}
            columns={invoiceColumns}
            loading={isLoadingInvoices}
            getId={(item) => item.id.toString()}
            emptyMessage="Nenhuma fatura encontrada para esta assinatura."
            pagination={invoicePagination}
            onPageChange={setInvoicePage}
            onLimitChange={setInvoiceLimit}
            onFiltersChange={() => {}}
            actions={(item) => (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleViewInvoice(item)}
                  className="btn-secondary flex items-center gap-2 text-sm"
                  title="Ver Detalhes"
                >
                  <EyeIcon className="h-4 w-4" />
                  Ver
                </button>
                <button
                  onClick={() => handleDownloadInvoicePDF(item)}
                  className="btn-secondary flex items-center gap-2 text-sm"
                  title="Baixar PDF"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  PDF
                </button>
              </div>
            )}
          />
        </div>
    </PageContainer>
  );
}

